import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import * as nutritionService from '../services/nutritionService';
import './NutritionPage.css';

const NutritionPage = () => {
  const { tokens } = useAuth();
  const { t } = useLanguage();

  const [view, setView] = useState('today'); // 'today', 'week', 'month'
  const [todayData, setTodayData] = useState(null);
  const [weekData, setWeekData] = useState([]);
  const [monthData, setMonthData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadNutritionData();
  }, [view]);

  const loadNutritionData = async () => {
    try {
      setLoading(true);

      if (view === 'today') {
        const data = await nutritionService.getTodayNutrition(tokens.access_token);
        setTodayData(data);
      } else if (view === 'week') {
        const data = await nutritionService.getWeekNutrition(tokens.access_token);
        setWeekData(data);
      } else if (view === 'month') {
        const data = await nutritionService.getMonthNutrition(tokens.access_token);
        setMonthData(data);
      }
    } catch (error) {
      console.error('Failed to load nutrition data:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateProgress = (current, target) => {
    if (!target) return 0;
    return Math.min((current / target) * 100, 100);
  };

  const renderTodayView = () => {
    if (!todayData) {
      return (
        <div className="empty-state">
          <div className="empty-icon">🥗</div>
          <h3>{t('no_data_today')}</h3>
          <p>{t('no_data_today_desc')}</p>
        </div>
      );
    }

    return (
      <div className="nutrition-today">
        {/* Calories Card */}
        <div className="nutrition-card calories-card">
          <div className="card-header">
            <h3>
              <span className="card-icon">🔥</span>
              {t('calories')}
            </h3>
          </div>
          <div className="card-body">
            <div className="progress-circle">
              <svg viewBox="0 0 120 120">
                <circle
                  className="progress-bg"
                  cx="60"
                  cy="60"
                  r="50"
                  fill="none"
                  stroke="#f0f0f0"
                  strokeWidth="10"
                />
                <circle
                  className="progress-bar"
                  cx="60"
                  cy="60"
                  r="50"
                  fill="none"
                  stroke="#667eea"
                  strokeWidth="10"
                  strokeDasharray={`${calculateProgress(
                    todayData.total_calories,
                    todayData.target_calories
                  ) * 3.14} 314`}
                  strokeLinecap="round"
                  transform="rotate(-90 60 60)"
                />
              </svg>
              <div className="progress-text">
                <div className="progress-value">{todayData.total_calories || 0}</div>
                <div className="progress-label">/ {todayData.target_calories || 0}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Macros Cards */}
        <div className="macros-grid">
          <div className="nutrition-card">
            <div className="card-header">
              <h3>
                <span className="card-icon">💪</span>
                {t('protein')}
              </h3>
            </div>
            <div className="card-body">
              <div className="macro-value">{todayData.total_protein || 0}g</div>
              <div className="macro-target">
                {t('target')}: {todayData.target_protein || 0}g
              </div>
              <div className="progress-bar-horizontal">
                <div
                  className="progress-fill protein"
                  style={{
                    width: `${calculateProgress(
                      todayData.total_protein,
                      todayData.target_protein
                    )}%`,
                  }}
                ></div>
              </div>
            </div>
          </div>

          <div className="nutrition-card">
            <div className="card-header">
              <h3>
                <span className="card-icon">🍞</span>
                {t('carbs')}
              </h3>
            </div>
            <div className="card-body">
              <div className="macro-value">{todayData.total_carbs || 0}g</div>
              <div className="macro-target">
                {t('target')}: {todayData.target_carbs || 0}g
              </div>
              <div className="progress-bar-horizontal">
                <div
                  className="progress-fill carbs"
                  style={{
                    width: `${calculateProgress(
                      todayData.total_carbs,
                      todayData.target_carbs
                    )}%`,
                  }}
                ></div>
              </div>
            </div>
          </div>

          <div className="nutrition-card">
            <div className="card-header">
              <h3>
                <span className="card-icon">🧈</span>
                {t('fat')}
              </h3>
            </div>
            <div className="card-body">
              <div className="macro-value">{todayData.total_fat || 0}g</div>
              <div className="macro-target">
                {t('target')}: {todayData.target_fat || 0}g
              </div>
              <div className="progress-bar-horizontal">
                <div
                  className="progress-fill fat"
                  style={{
                    width: `${calculateProgress(todayData.total_fat, todayData.target_fat)}%`,
                  }}
                ></div>
              </div>
            </div>
          </div>
        </div>

        {/* Meals */}
        {todayData.meals && todayData.meals.length > 0 && (
          <div className="meals-section">
            <h3>{t('todays_meals')}</h3>
            <div className="meals-list">
              {todayData.meals.map((meal, index) => (
                <div key={index} className="meal-card">
                  <div className="meal-type">{t(meal.meal_type)}</div>
                  <div className="meal-name">{meal.food_name}</div>
                  <div className="meal-nutrition">
                    <span>🔥 {meal.calories} {t('kcal')}</span>
                    <span>💪 {meal.protein}g</span>
                    <span>🍞 {meal.carbs}g</span>
                    <span>🧈 {meal.fat}g</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderWeekView = () => {
    if (weekData.length === 0) {
      return (
        <div className="empty-state">
          <div className="empty-icon">📊</div>
          <h3>{t('no_data_week')}</h3>
          <p>{t('no_data_week_desc')}</p>
        </div>
      );
    }

    return (
      <div className="nutrition-week">
        <div className="week-chart">
          {weekData.map((day, index) => (
            <div key={index} className="day-column">
              <div className="day-bar-container">
                <div
                  className="day-bar"
                  style={{
                    height: `${(day.total_calories / 3000) * 100}%`,
                  }}
                >
                  <span className="bar-value">{day.total_calories}</span>
                </div>
              </div>
              <div className="day-label">{new Date(day.date).toLocaleDateString('en', { weekday: 'short' })}</div>
            </div>
          ))}
        </div>

        <div className="week-summary">
          <div className="summary-card">
            <div className="summary-label">{t('avg_calories')}</div>
            <div className="summary-value">
              {Math.round(weekData.reduce((sum, day) => sum + day.total_calories, 0) / weekData.length)}
            </div>
          </div>
          <div className="summary-card">
            <div className="summary-label">{t('total_calories')}</div>
            <div className="summary-value">
              {weekData.reduce((sum, day) => sum + day.total_calories, 0)}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderMonthView = () => {
    if (monthData.length === 0) {
      return (
        <div className="empty-state">
          <div className="empty-icon">📅</div>
          <h3>{t('no_data_month')}</h3>
          <p>{t('no_data_month_desc')}</p>
        </div>
      );
    }

    return (
      <div className="nutrition-month">
        <div className="month-grid">
          {monthData.map((day, index) => (
            <div
              key={index}
              className={`month-cell ${day.total_calories > 0 ? 'has-data' : ''}`}
              style={{
                backgroundColor:
                  day.total_calories > 0
                    ? `rgba(102, 126, 234, ${Math.min(day.total_calories / 3000, 1)})`
                    : '#f8f9fa',
              }}
            >
              <div className="cell-date">{new Date(day.date).getDate()}</div>
              {day.total_calories > 0 && (
                <div className="cell-calories">{day.total_calories}</div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="nutrition-page">
      <div className="nutrition-header">
        <h1>{t('nutrition_tracking')}</h1>
        <p className="nutrition-subtitle">{t('nutrition_subtitle')}</p>
      </div>

      {/* View Tabs */}
      <div className="view-tabs">
        <button
          className={`tab ${view === 'today' ? 'tab-active' : ''}`}
          onClick={() => setView('today')}
        >
          {t('today')}
        </button>
        <button
          className={`tab ${view === 'week' ? 'tab-active' : ''}`}
          onClick={() => setView('week')}
        >
          {t('week')}
        </button>
        <button
          className={`tab ${view === 'month' ? 'tab-active' : ''}`}
          onClick={() => setView('month')}
        >
          {t('month')}
        </button>
      </div>

      {/* Content */}
      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>{t('loading')}</p>
        </div>
      ) : (
        <div className="nutrition-content">
          {view === 'today' && renderTodayView()}
          {view === 'week' && renderWeekView()}
          {view === 'month' && renderMonthView()}
        </div>
      )}
    </div>
  );
};

export default NutritionPage;
